import { Dropdown, Field, FieldSlots, Input, makeStyles, Option, Text } from '@fluentui/react-components';
import { observer } from 'mobx-react-lite';
import React, { useMemo } from 'react';
import { useCommonStyles, useStores } from '../../../common/hooks';
import { DropdownOption, NominationPartnerType, PartnerType } from '../../../models';
import { FlexBox } from '../flex-box';
import './PartnerTypeSelectionDropdown.scss';

interface PartnerTypeSelectionDropdownProps extends Pick<FieldSlots, 'label' | 'validationMessage'> {
  partnerTypeOptions: DropdownOption<PartnerType>[];
  nominationPartnerTypes: NominationPartnerType[];
  required?: boolean;
  otherField?: {
    onGetOtherValue: (data: string) => void,
    validationMessageForOther?: FieldSlots['validationMessage'],
  }
  ariaLabelledBy?: string;
}

const useStyles = makeStyles({
  option: {
    textAlign: 'start',
  },
});

const PartnerTypeSelectionDropdownImpl: React.FC<PartnerTypeSelectionDropdownProps> = (props) => {
  const {
    partnerTypeOptions,
    nominationPartnerTypes,
    label,
    validationMessage,
    required,
    otherField,
    ariaLabelledBy,
  } = props;

  const commonStyles = useCommonStyles();
  const styles = useStyles();

  const {
    partnerTypeStore,
  } = useStores();

  const nominationPartnerTypesDropdownMap = useMemo(() => {
    const selectedOptions = nominationPartnerTypes.map((partnerType) => `${partnerType.partnerTypeId}`);
    const filteredPartnerTypes = partnerTypeOptions.filter((partnerType) => selectedOptions.includes(`${partnerType.value.partnerTypeId}`)).map((option) => option.value);
    return {
      valueToDisplay: filteredPartnerTypes.map((partnerType) => `${partnerType.displayName}`),
      selectedOptions,
    }
  }, [nominationPartnerTypes.join(',')]);

  const onPartnerTypeCheckedChange = (checkedIds: string[]): void => {
    const filteredPartnerTypes = partnerTypeOptions.filter((partnerType) => checkedIds.includes(`${partnerType.value.partnerTypeId}`)).map((option) => option.value);
    nominationPartnerTypes.splice(0, nominationPartnerTypes.length, ...filteredPartnerTypes);
  };

  return (
    <FlexBox
      direction='column'
      isFullWidth
      className='partner-type-selection-dropdown'
    >
      <Field
        required={required}
        label={label}
        validationMessage={validationMessage}
        validationMessageIcon={null}
      >
        <Dropdown
          multiselect
          className={commonStyles.inputField}
          value={nominationPartnerTypesDropdownMap.valueToDisplay.join(', ')}
          selectedOptions={nominationPartnerTypesDropdownMap.selectedOptions}
          onOptionSelect={(_, data) => onPartnerTypeCheckedChange(data.selectedOptions)}
          aria-labelledby={ariaLabelledBy}
        >
          {
            partnerTypeOptions.map((option) => (
              <Option
                key={`${option.value.partnerTypeId}`}
                value={`${option.value.partnerTypeId}`}
                text={option.value.displayName}
                className={styles.option}
              >
                <div>
                  <Text
                    weight='semibold'
                    size={200}
                    block
                  >
                    {option.value.displayName}
                  </Text>
                  <Text
                    size={200}
                    as='p'
                  >
                    {option.value.description}
                  </Text>
                </div>
              </Option>
            ))
          }
        </Dropdown>
      </Field>

      {
        !!otherField && (
          <Field
            required
            validationMessage={otherField?.validationMessageForOther}
            validationMessageIcon={null}
            label={{
              children: 'Other',
              className: commonStyles.inputFieldLabel,
            }}
          >
            <Input
              type='text'
              className={commonStyles.inputField}
              placeholder='Describe your partner type here'
              value={nominationPartnerTypes.find((el) => partnerTypeStore.isOtherPartnerType(el.partnerTypeId))?.description || ''}
              onChange={(_, data) => {
                otherField.onGetOtherValue(data.value);
              }}
            />
          </Field>
        )
      }
    </FlexBox>
  );
};

export const PartnerTypeSelectionDropdown = observer(PartnerTypeSelectionDropdownImpl);
